import type { Editor, EditorEvents } from '@tiptap/react';
import type { Node, Schema } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';

export function setStoredTextStyleMark(tr: Transaction, schema: Schema, attrs: Record<string, unknown>): void {
  const textStyleType = schema.marks.textStyle;
  if (textStyleType) {
    const otherMarks = (tr.storedMarks ?? []).filter((m) => m.type.name !== 'textStyle');
    tr.setStoredMarks([...otherMarks, textStyleType.create(attrs)]);
  }
}

export type TextStyleAttribute = 'fontSize' | 'fontFamily' | 'color';
type AttributeValue = string | number;
type GetParentAttributeFunction = (parent: Node | null) => string;

interface TextStyleConfig {
  attributeName: TextStyleAttribute;
  getParentAttribute: GetParentAttributeFunction;
  parseValue?: (value: string) => AttributeValue;
}

export const getParentAttribute = (parent: Node | null, attribute: string, textDefaultStyles) => {
  switch (parent?.type.name) {
    case 'heading': {
      const level = parent?.attrs.level;
      return textDefaultStyles[`h${level}`]?.[attribute];
    }
    case 'paragraph': {
      const tableStyle = parent?.attrs?.['data-table-style'];
      return textDefaultStyles[tableStyle ?? 'p']?.[attribute];
    }
    default:
      return undefined;
  }
};

export const createTextStyleHandler = ({ attributeName, getParentAttribute, parseValue = (value) => value }: TextStyleConfig) => {
  const getTextNodeAttribute = (node: Node): string | undefined => {
    const textStyleMark = node.marks.find((mark) => mark.type.name === 'textStyle');
    if (textStyleMark) {
      const value = textStyleMark.attrs[attributeName];
      return value;
    }
  };

  const updateSelectedAttribute = ({ editor }: EditorEvents['transaction'], setSelected: (value: string) => void) => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const valuesInSelection: ReturnType<typeof parseValue>[] = [];

    if (editor.state.selection.empty) {
      const storedValue: string = editor.state.storedMarks?.find((mark) => mark.type.name === 'textStyle')?.attrs[attributeName];

      if (storedValue) {
        valuesInSelection.push(parseValue(storedValue));
      } else {
        const $from = editor.state.selection.$from;
        const textStyleMark = $from.marks().find((mark) => mark.type.name === 'textStyle');
        const value = textStyleMark?.attrs[attributeName];

        if (value) {
          valuesInSelection.push(parseValue(value));
        } else {
          const parentValue = getParentAttribute($from.parent);
          if (parentValue !== undefined) valuesInSelection.push(parentValue);
        }
      }
    } else {
      editor.state.doc.nodesBetween(from, to, (node, _pos, parent) => {
        if (node.type.name === 'text') {
          const value = getTextNodeAttribute(node);
          if (value) {
            valuesInSelection.push(parseValue(value));
          } else {
            const parentValue = getParentAttribute(parent);
            if (parentValue !== undefined) valuesInSelection.push(parentValue);
          }
        }
      });
    }

    const valuesSet = new Set(valuesInSelection);
    if (valuesSet.size === 1) {
      setSelected(Array.from(valuesSet)[0].toString());
    } else {
      setSelected('');
    }
  };

  return updateSelectedAttribute;
};

export const getParentNodeType = (parent: Node): string => {
  if (parent?.attrs?.['data-table-style']) {
    return parent?.attrs?.['data-table-style'];
  }
  return parent.type.name === 'heading' ? `h${parent.attrs.level}` : 'p';
};

export const getNodeTypeFromSelection = (editor: Editor): string => {
  const { selection } = editor.state;

  if (!selection.$from) return 'p';

  if (selection instanceof NodeSelection) {
    const domNode = editor.view.nodeDOM(selection.from);

    if (domNode instanceof HTMLElement) {
      if (domNode.tagName.toLowerCase() === 'span') {
        return 'spanElement';
      }
    }
  }

  return getParentNodeType(selection.$from.parent);
};
